import { DialogComponent } from '@theme/dialog';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class HeaderMenuDialog extends DialogComponent {
  requiredRefs = ['dialog', 'trigger', 'rootPanel'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  /** @type {string | null} */
  #activeMenuId = null;

  /** @type {string | null} */
  #activeSubmenuKey = null;

  /**
   * Steps one level back from the dialog-header back button (mobile):
   * closes the level-3 panel when one is open, the level-2 menu otherwise.
   */
  menuBack() {
    if (this.#activeSubmenuKey) {
      this.#closeActiveSubmenu();
      return;
    }

    this.#closeActiveMenu();
  }

  /** @param {CustomEvent} _event */
  #handleDialogOpen = (_event) => {
    this.#trigger?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      this.#focusFirst(this.#rootPanel);
    });
  };

  /** @param {CustomEvent} _event */
  #handleDialogClose = (_event) => {
    this.#closeActiveMenu({ restoreFocus: false });
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#trigger?.focus();
  };

  /** @param {CustomEvent} event */
  #handleBlockSelect = (event) => {
    const item = /** @type {HTMLElement} */ (event.target)?.closest('[data-menu-item]');
    if (!item) return;

    this.showDialog({ immediate: true });

    const trigger = item.querySelector('[data-menu-open]');
    if (trigger instanceof HTMLButtonElement) {
      this.#openMenu(trigger);
    }
  };

  /** @param {CustomEvent} _event */
  #handleBlockDeselect = (_event) => {
    this.#closeActiveMenu({ restoreFocus: false });
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    // Horizontal-bar item: open the drawer drilled into its category.
    const barOpen = event.target.closest('[data-open-menu]');
    if (barOpen instanceof HTMLElement) {
      const triggerId = barOpen.getAttribute('data-open-menu');
      const trigger = triggerId ? this.querySelector(`#${CSS.escape(triggerId)}[data-menu-open]`) : null;
      if (trigger instanceof HTMLButtonElement) {
        this.showDialog();
        this.#openMenu(trigger);
      }
      return;
    }

    const submenuOpen = event.target.closest('[data-submenu-open]');
    if (submenuOpen instanceof HTMLButtonElement) {
      this.#openSubmenu(submenuOpen);
      return;
    }

    const openButton = event.target.closest('[data-menu-open]');
    if (openButton instanceof HTMLButtonElement) {
      this.#openMenu(openButton);
      return;
    }

    const backButton = event.target.closest('[data-menu-back]');
    if (backButton instanceof HTMLButtonElement) {
      this.#closeActiveMenu();
    }
  };

  /**
   * Intercepts Escape to close the deepest open layer first (level 3, then
   * level 2) instead of the dialog. Relies on this handler being registered in
   * connectedCallback() (before DialogComponent registers its own in
   * showDialog()) so that stopImmediatePropagation() prevents the parent from
   * also closing the dialog.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#activeSubmenuKey) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#closeActiveSubmenu();
      return;
    }

    if (this.#activeMenuId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#closeActiveMenu();
    }
  };

  /** @param {HTMLButtonElement} trigger */
  #openMenu(trigger) {
    const menuId = trigger.getAttribute('aria-controls');
    if (!menuId) return;

    if (this.#activeMenuId === menuId) {
      return;
    }

    this.#closeActiveMenu({ restoreFocus: false });

    const panel = this.querySelector(`#${CSS.escape(menuId)}`);
    const item = trigger.closest('[data-menu-item]');

    if (!(panel instanceof HTMLElement) || !(item instanceof HTMLElement)) {
      return;
    }

    this.#activeMenuId = menuId;
    item.dataset.menuActive = 'true';
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.#setMenuIsolation(item);
    this.#setGlobalBack(this.#activeMenuLabel);

    requestAnimationFrame(() => {
      this.#focusFirst(panel);
    });
  }

  #closeActiveMenu({ restoreFocus = true } = {}) {
    this.#closeActiveSubmenu({ restoreFocus: false });

    const activeMenu = this.#activeMenu;
    if (!activeMenu) return;

    const { item, trigger, panel } = activeMenu;

    delete item.dataset.menuActive;
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    this.#activeMenuId = null;
    this.#clearMenuIsolation();
    this.#setGlobalBack(null);

    if (restoreFocus) {
      trigger.focus();
    }
  }

  /** @param {HTMLButtonElement} trigger */
  #openSubmenu(trigger) {
    const key = trigger.dataset.submenuOpen;
    if (!key) return;

    if (this.#activeSubmenuKey === key) {
      this.#closeActiveSubmenu();
      return;
    }

    this.#closeActiveSubmenu({ restoreFocus: false });

    const item = trigger.closest('[data-submenu-item]');
    const panels = this.#panelsForKey(key);

    if (!(item instanceof HTMLElement) || panels.length === 0) {
      return;
    }

    this.#activeSubmenuKey = key;
    item.dataset.submenuActive = 'true';
    trigger.setAttribute('aria-expanded', 'true');
    for (const panel of panels) {
      panel.hidden = false;
    }

    const submenuLabel = trigger.querySelector('.header-menu-item__submenu-trigger-label')?.textContent?.trim() ?? '';
    this.#setGlobalBack(this.#activeMenuLabel, submenuLabel);

    requestAnimationFrame(() => {
      const visible = panels.find((panel) => panel.offsetParent !== null);
      this.#focusFirst(visible ?? null);
    });
  }

  #closeActiveSubmenu({ restoreFocus = true } = {}) {
    const key = this.#activeSubmenuKey;
    if (!key) return;

    const panels = this.#panelsForKey(key);
    const item = this.querySelector('[data-submenu-item][data-submenu-active]');
    const trigger = item?.querySelector('[data-submenu-open]');

    for (const panel of panels) {
      panel.hidden = true;
    }

    if (item instanceof HTMLElement) {
      delete item.dataset.submenuActive;
    }
    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    this.#activeSubmenuKey = null;

    if (this.#activeMenuId) {
      this.#setGlobalBack(this.#activeMenuLabel);
    } else {
      this.#setGlobalBack(null);
    }

    if (restoreFocus && trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]}
   */
  #panelsForKey(key) {
    /** @type {HTMLElement[]} */
    const panels = [];
    for (const panel of this.querySelectorAll(`[data-submenu-panel="${CSS.escape(key)}"]`)) {
      if (panel instanceof HTMLElement) {
        panels.push(panel);
      }
    }
    return panels;
  }

  /**
   * Shows the dialog-header back button with the given label, or hides it.
   * When a sub-label is provided (level 3), it is joined to the label by an
   * accent pipe — e.g. "Femme | Vêtements".
   * @param {string | null} label
   * @param {string | null} [sublabel]
   */
  #setGlobalBack(label, sublabel = null) {
    const button = this.refs.backButton;
    const labelElement = this.refs.backLabel;

    if (!(button instanceof HTMLElement) || !(labelElement instanceof HTMLElement)) {
      return;
    }

    if (!label) {
      labelElement.replaceChildren();
      button.hidden = true;
      return;
    }

    /** @type {Node[]} */
    const parts = [];
    if (sublabel) {
      // Level 3: dim the parent category, accent pipe, then the active sub-category.
      const parent = document.createElement('span');
      parent.className = 'header-menu-dialog__header-back-parent';
      parent.textContent = label;

      const separator = document.createElement('span');
      separator.className = 'header-menu-dialog__header-back-sep';
      separator.setAttribute('aria-hidden', 'true');
      separator.textContent = '|';

      parts.push(parent, separator, document.createTextNode(sublabel));
    } else {
      parts.push(document.createTextNode(label));
    }

    labelElement.replaceChildren(...parts);
    button.hidden = false;
  }

  get #activeMenuLabel() {
    const label = this.#activeMenu?.trigger.querySelector('.header-menu-item__label');
    return label?.textContent?.trim() ?? '';
  }

  /** @param {HTMLElement} activeItem */
  #setMenuIsolation(activeItem) {
    for (const item of this.#menuItems) {
      item.inert = item !== activeItem;
    }
  }

  #clearMenuIsolation() {
    for (const item of this.#menuItems) {
      item.inert = false;
    }
  }

  /** @param {HTMLElement | null} container */
  #focusFirst(container) {
    if (!(container instanceof HTMLElement)) return;

    const target = container.querySelector(FOCUSABLE_SELECTOR);
    if (target instanceof HTMLElement) {
      target.focus();
      return;
    }

    container.focus();
  }

  get #rootPanel() {
    return this.refs.rootPanel instanceof HTMLElement ? this.refs.rootPanel : null;
  }

  get #trigger() {
    return this.refs.trigger instanceof HTMLElement ? this.refs.trigger : null;
  }

  get #menuItems() {
    return [...this.querySelectorAll('[data-menu-item]')].filter((item) => item instanceof HTMLElement);
  }

  get #activeMenu() {
    if (!this.#activeMenuId) return null;

    const panel = this.querySelector(`#${CSS.escape(this.#activeMenuId)}`);
    if (!(panel instanceof HTMLElement)) return null;

    const item = panel.closest('[data-menu-item]');
    const trigger = item?.querySelector('[data-menu-open]');

    if (!(item instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) {
      return null;
    }

    return { item, trigger, panel };
  }
}

if (!customElements.get('header-menu-dialog')) {
  customElements.define('header-menu-dialog', HeaderMenuDialog);
}
